import { Box, Grid } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import MyTypography from "@saleor/components/MyTypography";
import Skeleton from "@saleor/components/Skeleton";
import { ContractQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { RelayToFlatNoEdge } from "@saleor/types";
import { formatDate } from "@saleor/utils/date";
import classNames from "classnames";
import React from "react";
import { useIntl } from "react-intl";

interface IContractProps {
  data: RelayToFlatNoEdge<ContractQuery["contract"]>;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem",
      marginBottom: "1.6rem",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "30%"
      }
    },
    boxRight: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem",
      marginBottom: "1.6rem",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "68%"
      }
    },
    wrapper: {
      display: "flex",
      justifyContent: "space-between"
    },
    textRight: {
      textAlign: "right",
      "& .MuiTypography-root": {
        color: theme.palette.grey[900]
      }
    },
    header: {
      padding: "0.5rem 1.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      flexDirection: "column",
      "& .MuiTypography-root": {
        color: "white"
      },
      "& .MuiTypography-caption": {
        fontSize: "1rem",
        lineHeight: "1.6rem"
      },
      "& .MuiTypography-h6": {
        lineHeight: "2.4rem"
      }
    },
    title: {
      color: theme.palette.info.main
    },
    color_900: {
      color: theme.palette.grey[900]
    },
    color_800: {
      color: theme.palette.grey[800]
    },
    searchBox: {
      padding: "1.6rem"
    },
    searchBoxMargin: {
      marginTop: "2rem"
    },
    customerDetail: {
      background: theme.palette.background.default,
      borderRadius: 4,
      padding: "9px 12px",
      marginBottom: 10
    }
  }),
  { name: "ContractNo" }
);

const ContractNo: React.FC<IContractProps> = ({ data }) => {
  const classes = useStyles();
  const intl = useIntl();
  const { user } = useUser();

  return (
    <>
      <div className={classes.wrapper}>
        <Box className={classes.root}>
          <Grid className={classes.header} container>
            <MyTypography variant="caption">
              {user.isStaff ? "Contract No." : "เลขที่ Contract"}
            </MyTypography>
            <MyTypography variant="h6">
              {data ? `${data.contractNo}` : <Skeleton />}
            </MyTypography>
          </Grid>
          <Grid className={classes.searchBox} container>
            <Grid
              container
              className={user.isStaff ? "" : classes.customerDetail}
            >
              <Grid item xs={5}>
                <MyTypography
                  variant="body3"
                  className={user.isStaff ? classes.title : ""}
                >
                  {intl.formatMessage({ defaultMessage: "รหัสลูกค้า" })}
                </MyTypography>
              </Grid>
              <Grid item xs={7} className={classes.textRight}>
                <MyTypography variant="body3">
                  {data ? data.customer.code : <Skeleton />}
                </MyTypography>
              </Grid>
            </Grid>
            <Grid
              container
              className={user.isStaff ? "" : classes.customerDetail}
            >
              <Grid item xs={5}>
                <MyTypography
                  variant="body3"
                  className={user.isStaff ? classes.title : ""}
                >
                  {intl.formatMessage({ defaultMessage: "ชื่อลูกค้า" })}
                </MyTypography>
              </Grid>
              <Grid item xs={7} className={classes.textRight}>
                <MyTypography variant="body3">
                  {data ? data?.customer?.name : <Skeleton />}
                </MyTypography>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.boxRight}>
          <Grid
            className={classNames(
              classes.searchBox,
              !user.isStaff && classes.searchBoxMargin
            )}
            container
          >
            <Grid item xs={6}>
              {user.isStaff && (
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <MyTypography variant="body3" className={classes.title}>
                      BU
                    </MyTypography>
                  </Grid>
                  <Grid item xs={8}>
                    <MyTypography variant="body3" className={classes.color_900}>
                      {data ? data?.company?.businessUnit?.name : <Skeleton />}
                    </MyTypography>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <MyTypography variant="body3" className={classes.title}>
                    Company
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_900}>
                    {data ? data?.company?.shortName : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <MyTypography variant="body3" className={classes.title}>
                    {intl.formatMessage({ defaultMessage: "ชื่อโครงการ" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? data.projectName : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              {!user.isStaff && (
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <MyTypography variant="body3" className={classes.title}>
                      {intl.formatMessage({
                        defaultMessage: "เงื่อนไขการชำระเงิน"
                      })}
                    </MyTypography>
                  </Grid>
                  <Grid item xs={8}>
                    <MyTypography variant="body3" className={classes.color_800}>
                      {data ? data.paymentTermDescTh : <Skeleton />}
                    </MyTypography>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    {intl.formatMessage({ defaultMessage: "วันที่เริ่มต้น" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? formatDate(data.startDate) : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    {intl.formatMessage({ defaultMessage: "วันที่สิ้นสุด" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? formatDate(data.endDate) : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              {user.isStaff && (
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <MyTypography variant="body3" className={classes.title}>
                      {intl.formatMessage({
                        defaultMessage: "เงื่อนไขการชำระเงิน"
                      })}
                    </MyTypography>
                  </Grid>
                  <Grid item xs={7}>
                    <MyTypography variant="body3" className={classes.color_800}>
                      {data ? (
                        `${data?.paymentTermKey} - ${data?.paymentTermDescTh}`
                      ) : (
                        <Skeleton />
                      )}
                    </MyTypography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      </div>
    </>
  );
};

export default ContractNo;
